/** @jsxImportSource @emotion/react */
import React, { useState, useRef } from 'react';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Button } from '@mui/material';
import {
  AccodianWrap,
  ControlBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { GuideBlock } from '../GuideComponent';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CrudCode } from 'models/common/Edit';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import { IconButton } from 'components/buttons/IconSVG';
import { ContentFlex } from 'components/layouts/ContentFlex';
import { ContentSection } from 'components/layouts/ContentSection';

const GridSettings = () => {
  const [expanded, setExpanded] = useState<string>('panel1');
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : '');
  };

  const sampleOneRef = useRef<any>();
  const sampleTwoRef = useRef<any>();
  const layoutDefinition = [
    {
      binding: 'crudKey',
      header: '상태',
      minWidth: 45,
      width: 45,
      cellTemplate: GridStatusCellTemplate,
    },
    {
      header: '코드정보',
      columns: [
        {
          binding: 'code',
          header: '코드',
          minWidth: 80,
          width: '*',
        },
        {
          binding: 'codeNm',
          header: '코드명',
          minWidth: 80,
          width: '*',
        },
      ],
    },
    {
      binding: 'sort',
      header: '순서',
      minWidth: 80,
      width: '*',
    },
    {
      header: '사용자',
      columns: [
        {
          binding: 'userId',
          header: 'ID',
          minWidth: 80,
          width: '*',
        },
        {
          binding: 'userNm',
          header: '명',
          minWidth: 80,
          width: '*',
        },
        {
          binding: 'jtiNm',
          header: '직급',
          minWidth: 80,
          width: '*',
        },
      ],
    },
  ];

  const rowData = [
    {
      crudKey: CrudCode.CREATE,
      code: 'A',
      codeNm: '에이',
      sort: 1,
      userId: 'aa',
      userNm: '김에이',
      jtiNm: '사원',
    },
    {
      crudKey: CrudCode.UPDATE,
      code: 'B',
      codeNm: '비',
      sort: 2,
      userId: 'bb',
      userNm: '이비',
      jtiNm: '선임',
    },
    {
      crudKey: CrudCode.DELETE,
      code: 'C',
      codeNm: '씨',
      sort: 3,
      userId: 'cc',
      userNm: '박씨',
      jtiNm: '책임',
    },
    {
      crudKey: CrudCode.READ,
      code: 'D',
      codeNm: '디',
      sort: 4,
      userId: 'dd',
      userNm: '최디',
      jtiNm: '총괄',
    },
  ];

  return (
    <>
      <GuideBlock title={'<GridSettingButtons> Import'} isCode={true}>
        {`import GridSettingButtons from 'components/grids/GridSettingButtons';`}
      </GuideBlock>
      <GuideBlock title={'<GridSettingButtons> 속성'} isCode={true}>
        {`
interface Props {
  gridId?: string; // 그리드ID (한 페이지 내 2개 이상의 그리드에 설정버튼을 적용하는 경우 필수)
  layoutDefinition: any[]; // grid layoutDefinition
  changeShownColumns: (result: any[]) => void; // 노출필드 변경 이벤트 (선택된 값 반환)
}
              `}
      </GuideBlock>
      <Alert severity="warning">
        속성값 중 gridId는 한 페이지 내 2개 이상의 그리드에 설정버튼을 적용하는 경우 필수로
        전달해야함
        <br />
        <br /># 각 업무화면에서 작업해야 하는 내용
        <br />
        1. CustomGrid 태그 내 id, ref 속성 주입 필요
        <br />
        2. GridSettingButton 추가 필요
      </Alert>
      <AccodianWrap className="marginTop">
        <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
          <AccordionSummary>GridSettingButtons</AccordionSummary>
          <AccordionDetails>
            <GuideBlock>
              <ContentFlex>
                <ContentSection className="section marginT0 width45p">
                  <SubTitleLayout>
                    <SubTitleGroup>
                      <h3>1번 그리드</h3>
                      <span className="total">
                        총 <span>{(rowData || []).length.toLocaleString()}</span>건
                      </span>
                    </SubTitleGroup>
                    <ControlBtnGroup>
                      <Button
                        css={IconButton.button}
                        className="refresh"
                        onClick={() => console.log('-')}
                        disableRipple
                      >
                        새로고침
                      </Button>
                      <GridSettingButtons
                        gridId={'sampleOne'}
                        layoutDefinition={layoutDefinition}
                        changeShownColumns={(result) => {
                          sampleOneRef.current?.changeColumn?.(result);
                        }}
                      />
                    </ControlBtnGroup>
                  </SubTitleLayout>
                  <CustomGrid
                    id={'sampleOne'}
                    ref={sampleOneRef}
                    layoutDefinition={layoutDefinition}
                    rowData={rowData}
                    height={145}
                    isReadOnly={true}
                  />
                </ContentSection>
                <ContentSection className="section width50p">
                  <SubTitleLayout>
                    <SubTitleGroup>
                      <h3>2번 그리드</h3>
                      <span className="total">
                        총 <span>{(rowData || []).length.toLocaleString()}</span>건
                      </span>
                    </SubTitleGroup>
                    <ControlBtnGroup>
                      <Button
                        css={IconButton.button}
                        className="refresh"
                        onClick={() => console.log('-')}
                        disableRipple
                      >
                        새로고침
                      </Button>
                      <GridSettingButtons
                        gridId={'sampleTwo'}
                        layoutDefinition={layoutDefinition}
                        changeShownColumns={(result) => {
                          sampleTwoRef.current?.changeColumn?.(result);
                        }}
                      />
                    </ControlBtnGroup>
                  </SubTitleLayout>
                  <CustomGrid
                    id={'sampleTwo'}
                    ref={sampleTwoRef}
                    layoutDefinition={layoutDefinition}
                    rowData={rowData}
                    height={145}
                    isReadOnly={true}
                  />
                </ContentSection>
              </ContentFlex>
            </GuideBlock>
            <GuideBlock isCode={true}>
              {`
// 각 업무화면 내 소스작업
// TODO 1. ref 선언 (CustomGrid 내 함수 실행하기 위함)
const sampleOneRef = useRef<any>();
const layoutDefinition = [...]; // 그리드 레이아웃
... 
return ( 
    <SubTitleLayout>
        ...
        <ControlBtnGroup>
          // TODO 2. ControlBtnGroup 내 <GridSettingsButtons> 추가
          <GridSettingButtons
            gridId={'sampleOne'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => { // 노출필드 변경이벤트
                // ref를 이용해 <CustomGrid> 내 함수를 실행 
              sampleOneRef.current?.changeColumn?.(result);
            }}
          />
        </ControlBtnGroup>
    </SubTitleLayout>
    // TODO 3. <CustomGrid> 내 id, ref 속성 추가 
    <CustomGrid
        id={'sampleOne'} // <GridSettingButtons>에 선언한 gridId와 동일한 값
        ref={sampleOneRef} // ref 추가
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={145}
        isReadOnly={true}
    />
);
              `}
            </GuideBlock>
          </AccordionDetails>
        </Accordion>
      </AccodianWrap>
    </>
  );
};

export default GridSettings;
